import React from 'react';

import { decimalUpTo } from 'Helpers/currencyFormat';

/**
 * Denominator picker for ratio mode.
 *
 * Two labelled sections so a per-group (matched) figure can never be confused
 * with a project-wide (fixed) one. Denominators not compatible with the current
 * grouping dimension are disabled rather than dividing by an undefined value.
 *
 * @ticket IB-644
 */
export default function DenominatorPicker({ denominators = [], dimension, selected, onSelect }) {
    const matched = denominators.filter((d) => d.type === 'matched');
    const fixed = denominators.filter((d) => d.type === 'fixed');

    const isCompatible = (denominator) => (denominator.compatibleDimensions || []).includes(dimension);

    const renderOption = (denominator) => {
        const compatible = isCompatible(denominator);
        const isSelected = selected === denominator.key;

        return (
            <button
                type="button"
                key={denominator.key}
                disabled={!compatible}
                onClick={() => compatible && onSelect(denominator.key)}
                className={[
                    'tw-w-full tw-text-left tw-text-[13px] tw-px-2 tw-py-1.5 tw-rounded-md tw-flex tw-items-center tw-gap-2',
                    isSelected ? 'tw-bg-blue-25 tw-text-dark-blue tw-font-medium' : 'tw-text-charcoal',
                    compatible ? 'hover:tw-bg-charcoal-10' : 'tw-text-charcoal-25 tw-cursor-not-allowed',
                ].join(' ')}
            >
                <span>{$t(denominator.label)}</span>
                {denominator.value != null && (
                    <span className="tw-ml-auto tw-text-[11px] tw-text-charcoal-50 tw-font-light">{decimalUpTo(denominator.value, 0)}</span>
                )}
            </button>
        );
    };

    return (
        <div className="tw-bg-white tw-border tw-border-charcoal-25 tw-rounded-lg tw-p-1.5 tw-shadow-lg tw-w-72">
            <div className="tw-px-2.5 tw-py-2">
                <h5 className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-mb-2 tw-flex tw-items-center tw-gap-1.5">
                    {$t('Per this {{dimension}}', { dimension: $t(dimension) })}
                    <span className="tw-text-blue tw-bg-blue-25 tw-rounded tw-px-1.5 tw-py-0.5 tw-text-[9px]">{$t('matched')}</span>
                </h5>
                {matched.map(renderOption)}
            </div>
            <div className="tw-h-px tw-bg-charcoal-25 tw-mx-1.5" />
            <div className="tw-px-2.5 tw-py-2">
                <h5 className="tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-mb-2 tw-flex tw-items-center tw-gap-1.5">
                    {$t('Fixed total')}
                    <span className="tw-text-green-75 tw-bg-green-25 tw-rounded tw-px-1.5 tw-py-0.5 tw-text-[9px]">{$t('fixed')}</span>
                </h5>
                {fixed.map(renderOption)}
            </div>
        </div>
    );
}
